import { cn } from '@/lib/utils';
import { X } from 'lucide-react-native';
import * as React from 'react';
import {
  Modal,
  Platform,
  Pressable,
  useWindowDimensions,
  View,
  type ViewProps,
} from 'react-native';
import Animated, {
  FadeIn,
  FadeOut,
  SlideInDown,
  SlideOutDown,
} from 'react-native-reanimated';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { LayoutBreakpoints } from '@/constants/LayoutBreakpoints';
import { Icon } from '@/components/ui/icon';
import { useKeyboard } from '~/global/hooks/useKeyboard';

interface DrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  children: React.ReactNode;
}

function Drawer({ open, onOpenChange, children }: DrawerProps) {
  const { width, height } = useWindowDimensions();
  const { top } = useSafeAreaInsets();
  const isDesktop = width >= LayoutBreakpoints.tablet;

  return (
    <Modal
      visible={open}
      transparent
      animationType="none"
      onRequestClose={() => onOpenChange(false)}
      statusBarTranslucent
    >
      {/* Static backdrop */}
      <View
        style={{
          flex: 1,
          backgroundColor: 'rgba(0,0,0,0.5)',
          justifyContent: isDesktop ? 'center' : 'flex-end',
          alignItems: isDesktop ? 'center' : 'stretch',
        }}
      >
        <Pressable
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
          }}
          onPress={() => onOpenChange(false)}
        />

        {/* Animated content */}
        <Animated.View
          entering={
            isDesktop ? FadeIn.duration(200) : SlideInDown.duration(300)
          }
          exiting={
            isDesktop ? FadeOut.duration(150) : SlideOutDown.duration(250)
          }
          style={
            isDesktop
              ? {
                  width: 560,
                  maxHeight: height * 0.85,
                  minHeight: height * 0.5,
                }
              : {
                  // Almost-fullscreen: spans from just below the status bar
                  // down to the bottom screen edge. The bottom inset (and any
                  // keyboard avoidance) is applied inside DrawerContent so the
                  // background fills to the edge with no gap.
                  height: height - top,
                }
          }
        >
          {children}
        </Animated.View>
      </View>
    </Modal>
  );
}

function DrawerContent({ className, children, style, ...props }: ViewProps) {
  const { width } = useWindowDimensions();
  const { bottom } = useSafeAreaInsets();
  const keyboard = useKeyboard();
  const isDesktop = width >= LayoutBreakpoints.tablet;

  return (
    <View
      className={cn(
        'bg-background flex-1 flex flex-col shadow-lg shadow-black/5',
        isDesktop ? 'rounded-2xl border border-border' : 'rounded-t-3xl',
        className,
      )}
      // On mobile the drawer is fullscreen: the background fills the whole
      // screen while the content is inset off the bottom edge. Normally that
      // inset is the home-indicator safe area; while the keyboard is open it
      // becomes the keyboard height, lifting the scroll area and any pinned
      // footer above the keyboard. The drawer itself never resizes — only its
      // content insets — and the background still fills behind the keyboard.
      style={[
        !isDesktop && {
          paddingBottom: keyboard.visible ? keyboard.height : bottom,
        },
        style,
      ]}
      {...props}
    >
      {children}
    </View>
  );
}

function DrawerHeader({
  className,
  onClose,
  children,
  ...props
}: ViewProps & { onClose?: () => void }) {
  return (
    <View
      className={cn(
        'flex flex-row items-center justify-between px-5 py-4',
        className,
      )}
      {...props}
    >
      <View className="flex-1">{children}</View>
      {onClose && (
        <Pressable
          onPress={onClose}
          className="flex items-center justify-center rounded-full bg-surface-elevated w-8 h-8 active:opacity-70"
        >
          <Icon as={X} size={18} className="text-text-muted" />
        </Pressable>
      )}
    </View>
  );
}

function DrawerTitle({ className, children, ...props }: ViewProps) {
  return (
    <View className={cn('', className)} {...props}>
      {children}
    </View>
  );
}

function DrawerBody({ className, ...props }: ViewProps) {
  return <View className={cn('flex-1', className)} {...props} />;
}

export { Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerBody };
